import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faFilter, faStar, faMapMarkerAlt, faStore, faTimes } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import VendorCard from './VendorCard';
import VendorDetailModal from './VendorDetailModal';

const CATEGORIES = [
  'Catering',
  'Photography',
  'Venue',
  'Music',
  'Floral',
  'Rabbi/Officiant',
  'Planning',
  'Transportation',
  'Entertainment',
  'Other'
];

const SORT_OPTIONS = [
  { value: 'rating', label: 'Highest Rated' },
  { value: 'reviews', label: 'Most Reviews' },
  { value: 'price-low', label: 'Price: Low to High' },
  { value: 'price-high', label: 'Price: High to Low' },
  { value: 'newest', label: 'Newest' }
];

export default function VendorMarketplace({ 
  experienceId, 
  selectedTask, 
  onSelectVendor, 
  onClose 
}) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [sortBy, setSortBy] = useState('rating');
  const [priceRange, setPriceRange] = useState([0, 10000]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [location, setLocation] = useState('');

  useEffect(() => {
    loadVendors();
  }, [selectedCategory, sortBy, priceRange, location]);

  const loadVendors = async () => {
    setLoading(true);
    try {
      let q = collection(db, 'vendors');
      
      // Apply filters
      if (selectedCategory) {
        q = query(q, where('categories', 'array-contains', selectedCategory));
      }
      
      if (location) {
        q = query(q, where('location', '==', location));
      }

      // Apply sorting
      if (sortBy === 'rating') {
        q = query(q, orderBy('averageRating', 'desc'));
      } else if (sortBy === 'reviews') {
        q = query(q, orderBy('reviewCount', 'desc'));
      } else if (sortBy === 'newest') {
        q = query(q, orderBy('createdAt', 'desc'));
      }

      const snapshot = await getDocs(q);
      let vendorsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Filter by price range
      vendorsData = vendorsData.filter(vendor => {
        const minPrice = vendor.priceRange?.min || 0;
        const maxPrice = vendor.priceRange?.max || 10000;
        return maxPrice >= priceRange[0] && minPrice <= priceRange[1];
      });

      // Filter by search query
      if (searchQuery) {
        const queryLower = searchQuery.toLowerCase();
        vendorsData = vendorsData.filter(vendor =>
          vendor.name?.toLowerCase().includes(queryLower) ||
          vendor.description?.toLowerCase().includes(queryLower) ||
          vendor.categories?.some(cat => cat.toLowerCase().includes(queryLower))
        );
      }

      setVendors(vendorsData);
    } catch (error) {
      console.error('Error loading vendors:', error);
      toast.error('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectVendor = (vendor) => {
    if (onSelectVendor) {
      onSelectVendor(vendor, selectedTask);
    }
    setSelectedVendor(null);
    if (onClose) onClose();
  };

  const filteredVendors = vendors;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 z-[110] flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-text-primary mb-1">Vendor Marketplace</h2>
            <p className="text-sm text-text-secondary">
              Find vendors for {selectedTask?.title || 'your experience'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <FontAwesomeIcon icon={faTimes} className="text-text-secondary" />
          </button>
        </div>

        {/* Search and Filters */}
        <div className="p-6 border-b border-border bg-gray-50">
          {/* Search Bar */}
          <div className="relative mb-4">
            <FontAwesomeIcon 
              icon={faSearch} 
              className="absolute left-4 top-1/2 transform -translate-y-1/2 text-text-secondary"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search vendors..."
              className="w-full pl-12 pr-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Filter Toggle */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-border rounded-lg hover:border-primary transition-colors"
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>Filters</span>
            </button>

            {/* Category Pills */}
            <div className="flex-1 flex gap-2 overflow-x-auto">
              {CATEGORIES.map(category => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(selectedCategory === category ? null : category)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    selectedCategory === category
                      ? 'bg-primary text-white'
                      : 'bg-white border border-border text-text-secondary hover:border-primary'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Sort */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 border border-border rounded-lg bg-white focus:ring-2 focus:ring-primary focus:border-primary"
            >
              {SORT_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {/* Expanded Filters */}
          {showFilters && (
            <div className="mt-4 p-4 bg-white rounded-lg border border-border">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Price Range */}
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Price Range: ${priceRange[0]} - ${priceRange[1]}
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="10000"
                    step="100"
                    value={priceRange[1]}
                    onChange={(e) => setPriceRange([priceRange[0], parseInt(e.target.value)])}
                    className="w-full"
                  />
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-semibold text-text-primary mb-2">
                    Location
                  </label>
                  <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="City, State"
                    className="w-full px-4 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Vendor List */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-text-secondary">Loading vendors...</p>
            </div>
          ) : filteredVendors.length === 0 ? (
            <div className="text-center py-12">
              <FontAwesomeIcon icon={faStore} className="text-4xl text-text-secondary mb-4" />
              <p className="text-text-secondary mb-2">No vendors found</p>
              <p className="text-sm text-text-secondary">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredVendors.map(vendor => (
                <VendorCard
                  key={vendor.id}
                  vendor={vendor}
                  onClick={() => setSelectedVendor(vendor)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Vendor Detail Modal */}
      {selectedVendor && (
        <VendorDetailModal
          vendor={selectedVendor}
          experienceId={experienceId}
          selectedTask={selectedTask}
          onSelect={handleSelectVendor}
          onClose={() => setSelectedVendor(null)}
        />
      )}
    </div>,
    document.body
  );
}
